import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import cmsService from '../../services/cmsService';

// Async thunks for fetching CMS content
export const fetchCMSPage = createAsyncThunk(
  'cms/fetchPage',
  async ({ pageType, slug }, { rejectWithValue }) => {
    try {
      let response;
      
      switch (pageType) {
        case 'how-it-works':
          response = await cmsService.getHowItWorks();
          break;
        case 'how-it-works2':
          response = await cmsService.getHowItWorks2();
          break;
        case 'what-we-do':
          response = await cmsService.getWhatWeDo();
          break;
        case 'who-we-are':
          response = await cmsService.getWhoWeAre();
          break;
        case 'ways-to-fly':
          response = await cmsService.getWaysToFly();
          break;
        case 'faq':
          response = await cmsService.getFAQ();
          break;
        case 'safety-and-security':
          response = await cmsService.getSafetyAndSecurity();
          break;
        case 'news':
          response = await cmsService.getNews();
          break;
        case 'blog':
          response = await cmsService.getBlogs();
          break;
        case 'blog-post':
          response = await cmsService.getBlogPost(slug);
          break;
        case 'about-us':
          response = await cmsService.getAboutUs();
          break;
        case 'contact-us':
          response = await cmsService.getContactUs();
          break;
        case 'investor-relations':
          response = await cmsService.getInvestorRelations();
          break;
        case 'career':
          response = await cmsService.getCareer();
          break;
        case 'terms-and-conditions':
          response = await cmsService.getTermsAndConditions();
          break;
        case 'privacy-policy':
          response = await cmsService.getPrivacyPolicy();
          break;
        case 'cookie-policy':
          response = await cmsService.getCookiePolicy();
          break;
        case 'refund-and-cancellation':
          response = await cmsService.getRefundAndCancellation();
          break;
        case 'offer-terms':
          response = await cmsService.getOfferTerms();
          break;
        default:
          throw new Error('Invalid page type');
      }

      if (response.success) {
        return { pageType, data: response.data };
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const submitContactForm = createAsyncThunk(
  'cms/submitContactForm',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await cmsService.submitContactForm(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to submit contact form');
    }
  }
);

export const subscribeNewsletter = createAsyncThunk(
  'cms/subscribeNewsletter',
  async (email, { rejectWithValue }) => {
    try {
      const response = await cmsService.subscribeNewsletter(email);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to subscribe to newsletter');
    }
  }
);

// Initial state
const initialState = {
  pages: {}, // Store pages by type: { 'about-us': { data, loading, error }, ... }
  contactForm: {
    loading: false,
    success: false,
    error: null
  },
  newsletter: {
    loading: false,
    success: false,
    error: null
  },
  loading: false,
  error: null
};

// CMS slice
const cmsSlice = createSlice({
  name: 'cms',
  initialState,
  reducers: {
    clearCMSError: (state) => {
      state.error = null;
    },
    clearCMSPage: (state, action) => {
      const pageType = action.payload;
      if (state.pages[pageType]) {
        delete state.pages[pageType];
      }
    },
    resetContactForm: (state) => {
      state.contactForm = {
        loading: false,
        success: false,
        error: null
      };
    },
    resetNewsletter: (state) => {
      state.newsletter = {
        loading: false,
        success: false,
        error: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch CMS Page
      .addCase(fetchCMSPage.pending, (state, action) => {
        const pageType = action.meta.arg.pageType;
        state.pages[pageType] = {
          ...state.pages[pageType],
          loading: true,
          error: null
        };
        state.loading = true;
      })
      .addCase(fetchCMSPage.fulfilled, (state, action) => {
        const { pageType, data } = action.payload;
        state.pages[pageType] = {
          data,
          loading: false,
          error: null
        };
        state.loading = false;
      })
      .addCase(fetchCMSPage.rejected, (state, action) => {
        const pageType = action.meta.arg.pageType;
        state.pages[pageType] = {
          ...state.pages[pageType],
          loading: false,
          error: action.payload
        };
        state.loading = false;
        state.error = action.payload;
      })
      
      // Submit Contact Form
      .addCase(submitContactForm.pending, (state) => {
        state.contactForm.loading = true;
        state.contactForm.error = null;
        state.contactForm.success = false;
      })
      .addCase(submitContactForm.fulfilled, (state) => {
        state.contactForm.loading = false;
        state.contactForm.success = true;
      })
      .addCase(submitContactForm.rejected, (state, action) => {
        state.contactForm.loading = false;
        state.contactForm.error = action.payload;
      })
      
      // Subscribe Newsletter
      .addCase(subscribeNewsletter.pending, (state) => {
        state.newsletter.loading = true;
        state.newsletter.error = null;
        state.newsletter.success = false;
      })
      .addCase(subscribeNewsletter.fulfilled, (state) => {
        state.newsletter.loading = false;
        state.newsletter.success = true;
      })
      .addCase(subscribeNewsletter.rejected, (state, action) => {
        state.newsletter.loading = false;
        state.newsletter.error = action.payload;
      });
  }
});

export const { clearCMSError, clearCMSPage, resetContactForm, resetNewsletter } = cmsSlice.actions;
export default cmsSlice.reducer;
